import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import globe from "../../assets/globe_image.png";
import logo from "../../assets/Logo.png";
import background from "../../assets/background_image.jpg";
import { auth } from "../../firebase";
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, sendPasswordResetEmail } from "firebase/auth";

const SignIn = () => {
  const [activeTab, setActiveTab] = useState("login");
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [signupName, setSignupName] = useState("");
  const [signupEmail, setSignupEmail] = useState("");
  const [signupPassword, setSignupPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [signupError, setSignupError] = useState("");
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [forgotPasswordError, setForgotPasswordError] = useState("");
  const [forgotPasswordMessage, setForgotPasswordMessage] = useState("");
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const getCustomErrorMessage = (code) => {
    switch (code) {
      case "auth/invalid-email":
        return "Invalid email format. Please enter a valid email.";
      case "auth/user-not-found":
        return "No user found with this email.";
      case "auth/wrong-password":
        return "Incorrect password. Please try again.";
      case "auth/email-already-in-use":
        return "This email is already registered. Please login or use another email.";
      case "auth/weak-password":
        return "Password is too weak. Please use a stronger password.";
      case "auth/missing-password":
        return "Please enter a password.";
      default:
        return "An error occurred. Please try again.";
    }
  };

  const extractNameFromEmail = (email) => {
    if (email && email.includes("@")) {
      return email.split("@")[0];
    }
    return email;
  };

  const handleLogin = (e) => {
    e.preventDefault();
    setLoading(true);
    signInWithEmailAndPassword(auth, loginEmail, loginPassword)
      .then((userCredential) => {
        const user = userCredential.user;
        const username = extractNameFromEmail(user.email);
        sessionStorage.setItem("username", username);
        setLoginError("");
        setLoading(false);
        navigate("/landing-page");
      })
      .catch((err) => {
        const customMessage = getCustomErrorMessage(err.code);
        setLoginError(customMessage);
        setLoading(false);
      });
  };

  const handleSignup = (e) => {
    e.preventDefault();
    if (!signupName || !signupEmail || !signupPassword) {
      setSignupError("All fields are required!");
      return;
    }

    if (!signupEmail.endsWith("@mahindra.com")) {
      setSignupError("Please use a '@mahindra.com' email address.");
      return;
    }

    setLoading(true);
    createUserWithEmailAndPassword(auth, signupEmail, signupPassword)
      .then((userCredential) => {
        const user = userCredential.user;
        const username = extractNameFromEmail(user.email);
        sessionStorage.setItem("username", username);
        setSignupError("");
        setLoading(false);
        navigate("/home");
      })
      .catch((err) => {
        const customMessage = getCustomErrorMessage(err.code);
        setSignupError(customMessage);
        setLoading(false);
      });
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotPasswordError("");
    setForgotPasswordMessage("");

    if (!forgotPasswordEmail) {
      setForgotPasswordError("Please enter your email address.");
      return;
    }

    try {
      await sendPasswordResetEmail(auth, forgotPasswordEmail);
      setForgotPasswordMessage("Password reset email sent. Check your inbox!");
    } catch (err) {
      setForgotPasswordError(getCustomErrorMessage(err.code));
    }
  };

  return (
    <div
      className="relative min-h-screen flex justify-center items-center bg-cover bg-center p-4"
      style={{ backgroundImage: `url(${background})` }}
    >
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-black opacity-60"></div>

      <div className="relative z-10 bg-white bg-opacity-90 shadow-xl rounded-lg flex flex-col md:flex-row w-full max-w-3xl p-4 sm:p-8">
        {/* Left section with image */}
        <div className="w-full md:w-1/2 flex flex-col justify-center items-center from-gray-700 to-gray-900 text-white p-6 rounded-t-lg md:rounded-l-lg md:rounded-t-none">
          <img src={logo} alt="SustenSight Logo" className="w-32 sm:w-40 mb-4" />
          <img src={globe} alt="Globe" className="w-48 sm:w-64 md:w-72 mb-2" />
        </div>

        {/* Right section with form */}
        <div className="w-full md:w-1/2 flex flex-col justify-center items-center p-6 sm:p-2">
          {/* Tab navigation */}
          <div className="flex justify-between w-full max-w-md mb-6 border-b border-gray-300">
            <button
              onClick={() => setActiveTab("login")}
              className={`text-lg font-semibold pb-2 w-1/2 text-center transition-all ${
                activeTab === "login" ? "text-orange-500 border-b-2 border-orange-500" : "text-gray-600"
              }`}
            >
              Login
            </button>
            <button
              onClick={() => setActiveTab("signup")}
              className={`text-lg font-semibold pb-2 w-1/2 text-center transition-all ${
                activeTab === "signup" ? "text-orange-500 border-b-2 border-orange-500" : "text-gray-600"
              }`}
            >
              Sign Up
            </button>
          </div>

          {/* Error message */}
          {activeTab === "login" && loginError && (
            <div className="text-red-500 mb-4 text-center">{loginError}</div>
          )}
          {activeTab === "signup" && signupError && (
            <div className="text-red-500 mb-4 text-center">{signupError}</div>
          )}

          {/* Form */}
          {activeTab === "login" ? (
            <form className="w-full max-w-md flex flex-col items-center" onSubmit={handleLogin}>
              <div className="mb-4 w-full">
                <label htmlFor="loginEmail" className="block text-gray-700 font-semibold">Email</label>
                <input
                  type="text"
                  id="loginEmail"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring focus:ring-orange-200"
                  placeholder="Enter your email"
                />
              </div>

              <div className="mb-6 w-full">
                <label htmlFor="loginPassword" className="block text-gray-700 font-semibold">Password</label>
                <input
                  type="password"
                  id="loginPassword"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring focus:ring-orange-200"
                  placeholder="Enter your password"
                />
              </div>

              <button
                type="submit"
                className="w-full py-3 bg-orange-500 text-white font-semibold rounded-lg shadow-md hover:bg-orange-600 transition-all"
                disabled={loading}
              >
                {loading ? "Logging in..." : "Login"}
              </button>

              <button
                type="button"
                className="w-full mt-4 py-3 bg-gray-600 text-white font-semibold rounded-lg shadow-md hover:bg-gray-700 transition-all"
              >
                SSO Login
              </button>

              {/* Forgot Password Link */}
              <button
                type="button"
                onClick={() => setShowForgotPasswordModal(true)}
                className="mt-4 text-orange-500 hover:underline"
              >
                Forgot Password?
              </button>
            </form>
          ) : (
            <form className="w-full max-w-md flex flex-col items-center" onSubmit={handleSignup}>
              <div className="mb-4 w-full">
                <label htmlFor="signupName" className="block text-gray-700 font-semibold">Name</label>
                <input
                  type="text"
                  id="signupName"
                  value={signupName}
                  onChange={(e) => setSignupName(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring focus:ring-orange-200"
                  placeholder="Enter your name"
                />
              </div>

              <div className="mb-4 w-full">
                <label htmlFor="signupEmail" className="block text-gray-700 font-semibold">Email</label>
                <input
                  type="email"
                  id="signupEmail"
                  value={signupEmail}
                  onChange={(e) => setSignupEmail(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring focus:ring-orange-200"
                  placeholder="Enter your email"
                />
              </div>

              <div className="mb-6 w-full">
                <label htmlFor="signupPassword" className="block text-gray-700 font-semibold">Password</label>
                <input
                  type="password"
                  id="signupPassword"
                  value={signupPassword}
                  onChange={(e) => setSignupPassword(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring focus:ring-orange-200"
                  placeholder="Enter your password"
                />
              </div>

              <button
                type="submit"
                className="w-full py-3 bg-orange-500 text-white font-semibold rounded-lg shadow-md hover:bg-orange-600 transition-all"
                disabled={loading}
              >
                {loading ? "Signing up..." : "Sign Up"}
              </button>
            </form>
          )}
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPasswordModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-20">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold mb-4">Forgot Password</h2>
            <form onSubmit={handleForgotPassword}>
              <div className="mb-4">
                <label htmlFor="forgotPasswordEmail" className="block text-gray-700 font-semibold">Email</label>
                <input
                  type="email"
                  id="forgotPasswordEmail"
                  value={forgotPasswordEmail}
                  onChange={(e) => setForgotPasswordEmail(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring focus:ring-orange-200"
                  placeholder="Enter your email"
                />
              </div>
              {forgotPasswordError && (
                <div className="text-red-500 mb-4">{forgotPasswordError}</div>
              )}
              {forgotPasswordMessage && (
                <div className="text-green-500 mb-4">{forgotPasswordMessage}</div>
              )}
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => setShowForgotPasswordModal(false)}
                  className="mr-2 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
                >
                  Reset Password
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SignIn;